<script setup lang="ts">
  import { computed } from 'vue';
  import { storeToRefs } from 'pinia';
  import checkCircleIcon from '@/shared/assets/icons/CheckCircleIcon.svg';
  import { DonutChart, type DonutChartSegment } from '@/shared/ui';
  import { useDashboardStore } from '../../model/dashboard.store';
  import type { DashboardApprovalStatusEntry } from '../../model/dashboard.types';
  import { formatNumber, formatVariationPct } from '../../model/format';
  import { formatPeriodLabel } from '../../model/period';
  import VariationBadge from '../VariationBadge.vue';
  import WidgetCard from '../WidgetCard.vue';

  interface DonutBlock {
    title: string;
    segments: DonutChartSegment[];
    centerValue: string;
    centerLabel: string;
    variationPct: number | null;
    badgeText?: string;
  }

  const dashboardStore = useDashboardStore();
  const { data, isLoading, comparisonLabel, granularity, period } = storeToRefs(dashboardStore);

  const periodLabel = computed(() => formatPeriodLabel(granularity.value, period.value));

  function buildBadgeText(variationPct: number | null): string | undefined {
    if (variationPct === null || variationPct === 0) return undefined;
    const direction = variationPct > 0 ? 'mais' : 'menos';
    return `${formatVariationPct(variationPct)} ${direction} aprovações`;
  }

  function buildBlock(
    entry: DashboardApprovalStatusEntry | undefined,
    title: string,
    centerLabel: string,
    segmentLabels: { rejected: string; approved: string }
  ): DonutBlock {
    const total = entry?.total ?? 0;
    const variationPct = total > 0 ? (entry?.variationPct ?? null) : null;
    return {
      title,
      centerLabel,
      segments: [
        { label: segmentLabels.rejected, value: entry?.rejected ?? 0 },
        { label: segmentLabels.approved, value: entry?.approved ?? 0 },
      ],
      centerValue: formatNumber(total),
      variationPct,
      badgeText: buildBadgeText(variationPct),
    };
  }

  const blocks = computed<DonutBlock[]>(() => [
    buildBlock(data.value?.approvalStatus.documents, 'Documentos', 'Documentos enviados', {
      rejected: 'Reprovados',
      approved: 'Aprovados',
    }),
    buildBlock(data.value?.approvalStatus.records, 'Registros', 'Matrizes criadas', {
      rejected: 'Reprovadas',
      approved: 'Aprovadas',
    }),
  ]);
</script>

<template>
  <WidgetCard
    title="Status de Aprovações e Reprovações"
    :icon="checkCircleIcon"
    :period-label="periodLabel"
    :loading="isLoading"
    :has-data="data !== null"
    skeleton="donuts"
  >
    <div
      data-testid="approval-status-widget"
      class="flex h-full items-center gap-5"
    >
      <section
        v-for="(block, index) in blocks"
        :key="block.title"
        data-testid="approval-status-block"
        class="flex min-w-0 flex-1 flex-col gap-5"
        :class="index === 0 ? 'border-r border-gray-02 py-4 pl-4 pr-5' : 'p-4'"
      >
        <h3 class="text-sm font-normal text-gray-07">
          {{ block.title }}
        </h3>
        <DonutChart
          class="self-center"
          :segments="block.segments"
          :center-value="block.centerValue"
          :center-label="block.centerLabel"
        />
        <VariationBadge
          class="self-center"
          :variation-pct="block.variationPct"
          :text="block.badgeText"
          :comparison-label="comparisonLabel"
        />
      </section>
    </div>
  </WidgetCard>
</template>
